/**
 * Public E-Sign page — the counterparty (e.g. Medical Director candidate)
 * lands here from an email link, reviews the PDF preview embedded in the
 * page, types their legal name, ticks the two ESIGN Act consent boxes,
 * and executes the agreement. IP + timestamp are captured server-side
 * for the audit trail.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const container = {
  minHeight: "100vh",
  background: "linear-gradient(180deg,#faf5ff 0%, #fdf4ff 100%)",
  fontFamily: "'Inter', 'Outfit', system-ui, sans-serif",
  padding: "40px 16px",
};
const card = {
  maxWidth: 920, margin: "0 auto", background: "#ffffff",
  border: "1px solid #ede9fe", borderRadius: 16,
  boxShadow: "0 8px 24px rgba(76,29,149,0.08)",
  padding: 32,
};

export default function EsignPage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [err, setErr] = useState("");
  const [signatureName, setSignatureName] = useState("");
  const [printedName, setPrintedName] = useState("");
  const [agreeEsign, setAgreeEsign] = useState(false);
  const [agreeContents, setAgreeContents] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/esign/session/${token}`);
      setSession(data);
      if (!printedName && data?.party_name) setPrintedName(data.party_name);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Invalid or expired signing link");
    }
  }, [token, printedName]);

  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    if (!signatureName.trim() || signatureName.trim().length < 2) {
      toast.error("Type your full legal name to sign.");
      return;
    }
    if (!agreeEsign || !agreeContents) {
      toast.error("Please tick both consent checkboxes to proceed.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/esign/sign/${token}`, {
        signature_name: signatureName.trim(),
        printed_name: printedName.trim() || signatureName.trim(),
        agree_esign_act: agreeEsign,
        agree_contents: agreeContents,
      });
      toast.success("Agreement signed. A copy has been emailed to you.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (err) {
    return (
      <div style={container}>
        <Toaster position="top-center" />
        <div style={{ ...card, textAlign: "center" }}>
          <h1 style={{ color: "#c026d3", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Unable to load agreement</h1>
          <p style={{ color: "#64748b" }}>{err}</p>
        </div>
      </div>
    );
  }
  if (!session) {
    return <div style={container}><div style={{ ...card, textAlign: "center", color: "#64748b" }}>Loading agreement…</div></div>;
  }

  const isSigned = session.status === "signed";
  const isCancelled = session.status === "cancelled";
  const signedPdfUrl = `${API_BASE}/esign/signed/${token}.pdf`;
  const previewPdfUrl = `${API_BASE}/esign/preview/${token}.pdf`;

  return (
    <div style={container} data-testid="esign-page">
      <Toaster position="top-center" />
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg,#c026d3,#4c1d95)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800 }}>G</div>
          <div>
            <div style={{ fontSize: 12, letterSpacing: 1, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>GLPFlo Telehealth Platform</div>
            <h1 style={{ margin: 0, fontSize: 22, color: "#0b0221", fontWeight: 700 }}>Medical Director Consulting Agreement</h1>
          </div>
        </div>
        <p style={{ color: "#64748b", fontSize: 14, marginTop: 6 }}>
          Prepared for <b style={{ color: "#0b0221" }}>{session.party_name}</b>
          {session.credentials && <> · <span>{session.credentials}</span></>}
        </p>

        {isCancelled && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: 12, borderRadius: 10, marginTop: 12 }}>
            This agreement was cancelled by the sender. Please contact GLPFlo compliance.
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, margin: "20px 0", background: "#faf5ff", border: "1px solid #ede9fe", borderRadius: 12, padding: 16 }}>
          <Field label="Monthly Fee" value={session.monthly_fee ? `$${session.monthly_fee}` : "— (to be hand-written)"} />
          <Field label="Effective Date" value={session.effective_date || "—"} />
          <Field label="Initial Term" value={`${session.term_months} months`} />
          <Field label="Status" value={isSigned ? "SIGNED" : "Awaiting signature"} tone={isSigned ? "emerald" : "purple"} />
        </div>

        <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e5e7eb", marginBottom: 24 }}>
          <iframe
            src={isSigned ? signedPdfUrl : previewPdfUrl}
            title={isSigned ? "Signed agreement" : "Agreement preview"}
            style={{ width: "100%", height: 620, border: 0, background: "#f8fafc" }}
            data-testid="esign-pdf-iframe"
          />
        </div>

        {isSigned ? (
          <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", padding: 16, borderRadius: 12, textAlign: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>✓ Agreement signed on {new Date(session.signed_at).toLocaleString()}</div>
            <div style={{ marginTop: 4, fontSize: 13 }}>A copy has been emailed to you and to GLPFlo compliance.</div>
            <a href={signedPdfUrl} style={{ display: "inline-block", marginTop: 12, background: "#6d28d9", color: "#fff", padding: "10px 18px", borderRadius: 8, textDecoration: "none", fontWeight: 600 }} data-testid="esign-download-signed">Download signed PDF</a>
          </div>
        ) : !isCancelled ? (
          <div>
            <h2 style={{ fontSize: 16, color: "#0b0221", fontWeight: 700, marginTop: 4, marginBottom: 12 }}>Sign electronically</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Printed name</Label>
                <Input value={printedName} onChange={setPrintedName} placeholder="Dr. Jane Smith" testid="esign-printed-name" />
              </div>
              <div>
                <Label>Your electronic signature (type your full legal name)</Label>
                <Input value={signatureName} onChange={setSignatureName} placeholder="Type your full name to sign" testid="esign-signature-name" />
              </div>
            </div>
            {signatureName.trim() && (
              <div style={{ marginTop: 12, background: "#fdf4ff", border: "1px solid #f5d0fe", borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 10, letterSpacing: 1.4, color: "#a21caf", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Signature preview</div>
                <div style={{ fontFamily: "'Great Vibes','Brush Script MT',cursive", fontSize: 34, color: "#4c1d95", lineHeight: 1 }} data-testid="esign-signature-preview">{signatureName.trim()}</div>
              </div>
            )}
            <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <Consent checked={agreeEsign} onChange={setAgreeEsign} testid="esign-agree-esign">
                <b>ESIGN Act consent.</b> I agree to conduct this transaction electronically and understand that my typed signature has the same legal effect as a wet-ink signature.
              </Consent>
              <Consent checked={agreeContents} onChange={setAgreeContents} testid="esign-agree-contents">
                <b>Terms.</b> I have reviewed the agreement above and agree to be bound by its terms, including the non-prescribing scope and the monthly fee shown.
              </Consent>
            </div>
            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={sign}
                disabled={submitting || !signatureName.trim() || !agreeEsign || !agreeContents}
                data-testid="esign-submit"
                style={{
                  background: submitting || !signatureName.trim() || !agreeEsign || !agreeContents ? "#c4b5fd" : "linear-gradient(135deg,#c026d3,#6d28d9)",
                  color: "#fff", padding: "12px 24px", borderRadius: 10, border: 0, fontWeight: 700, fontSize: 15,
                  cursor: submitting || !signatureName.trim() || !agreeEsign || !agreeContents ? "not-allowed" : "pointer",
                  boxShadow: "0 4px 12px rgba(76,29,149,0.25)",
                }}
              >
                {submitting ? "Signing…" : "Sign & Execute"}
              </button>
            </div>
            <div style={{ marginTop: 12, fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
              Your IP address, timestamp, and browser fingerprint will be recorded in the executed PDF audit block.
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Field({ label, value, tone = "slate" }) {
  const color = tone === "emerald" ? "#059669" : tone === "purple" ? "#6d28d9" : "#0b0221";
  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#64748b", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ color, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
function Label({ children }) {
  return <label style={{ display: "block", fontSize: 11, letterSpacing: 1, color: "#64748b", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>{children}</label>;
}
function Input({ value, onChange, placeholder, testid }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 14, outline: "none" }}
    />
  );
}
function Consent({ checked, onChange, children, testid }) {
  return (
    <label style={{ display: "flex", gap: 10, alignItems: "flex-start", background: checked ? "#f0fdf4" : "#fff", border: `1px solid ${checked ? "#86efac" : "#e5e7eb"}`, padding: 12, borderRadius: 10, cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} style={{ marginTop: 3 }} />
      <span style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{children}</span>
    </label>
  );
}
